import { chunk, uniq } from 'lodash';
import { Project, PackageLine, PageCoordinates, Package, PackageState } from '../models';
import {
    ProjectResult,
    PackageStateResult,
    SearchResult,
    CommitLabelsequest,
    PackagesRequest,
    PackageLinesResponse,
    PackagesResponse,
    SearchPackagesRequest,
    PackageProblemReport,
    PackageResult
} from '../types';
import security from './SecurityService';
import { ProjectKeywordRenameModel } from '../models';
import ProjectImportModel from '../../project_management/models/ProjectImportModel';
import appClient, { ResultApi } from './AppClient';
import { Utils } from './Utils';
import { endpoints } from '../../../config';
import {
    FeatureFlagProcessingPlan,
    PackageFeatureFlagsPreview
} from '../../project_management/models/FeatureFlagProcessingPlan';
import { PackageProcessingResult } from '../types/PackageProcessingResult';

const stateFilterValues = {
    [PackageState.Ready]: [PackageStateResult.Ready],
    [PackageState.Busy]: [PackageStateResult.Importing, PackageStateResult.Processing, PackageStateResult.Parsed],
    [PackageState.Broken]: [PackageStateResult.Broken]
};

type SearchPackagesRestResult = {
    packages: Array<Partial<PackageResult> & Pick<PackageResult, 'id' | 'fileName' | 'state'>>;
    total: number;
};

type ProjectRestResult = Partial<ProjectResult> & Pick<ProjectResult, 'id' | 'name' | 'keywords' | 'color'>;

type ProjectPackagesRestResult = Pick<ProjectResult, 'id'> & {
    packages: ProjectResult['packages'];
};

type ProjectTagRestResult = {
    tag: string;
};

export default class ProjectsService {
    async getProjectsResult(
        state: PackageStateResult | null = PackageStateResult.Ready
    ): Promise<ResultApi<ProjectResult[]>> {
        const result = await appClient.get<ProjectRestResult[]>(this.getProjectsUrl(state, true));
        return result.map(projects => projects.map(this.mapProjectResult));
    }

    async getProjects(state: PackageStateResult | null = PackageStateResult.Ready): Promise<ProjectResult[]> {
        return (await this.getProjectsResult(state)).unwrapOr([]);
    }

    async getProjectsForCurrentUser(
        state: PackageStateResult | null = PackageStateResult.Ready
    ): Promise<ProjectResult[]> {
        const result = await appClient.get<ProjectRestResult[]>(this.getProjectsUrl(PackageStateResult.Ready));
        const projects = result.map(projectResults => projectResults.map(this.mapProjectResult)).unwrapOr([]);

        if (!state || projects.length === 0) {
            return projects;
        }

        return await Promise.all(
            projects.map(async project => {
                const packagesResult = await appClient.get<ProjectPackagesRestResult | null>(
                    `${endpoints.manageUrl}projects/${project.id}/packages?state=${encodeURIComponent(state)}`
                );

                return {
                    ...project,
                    packages: packagesResult.unwrapOr(null)?.packages || []
                };
            })
        );
    }

    async getProject(id: string): Promise<ProjectResult | null> {
        const result = await appClient.get<ProjectRestResult | null>(`${endpoints.manageUrl}projects/${id}`);

        if (result.isErr()) {
            throw result.error;
        }

        return result.value ? this.mapProjectResult(result.value) : null;
    }

    // eslint-disable-next-line max-len
    async getPackages(
        project: Project,
        state: PackageStateResult = PackageStateResult.Ready
    ): Promise<ProjectResult | null> {
        const url = `${endpoints.manageUrl}projects/${project.id}/packages?state=${encodeURIComponent(state)}`;
        const result = await appClient.get<ProjectPackagesRestResult | null>(url);

        if (result.isErr() || !result.value) {
            return null;
        }

        return this.mapProjectResult({
            id: result.value.id,
            name: project.title,
            color: project.color,
            keywords: project.keywords,
            type: project.type,
            packages: result.value.packages
        });
    }

    async addProject(proj: Project): Promise<ResultApi<string>> {
        const request = {
            name: proj.title,
            type: proj.type,
            keywords: proj.keywords,
            color: proj.color,
            featureFlags: proj.featureFlags
        };

        const formData = new FormData();
        for (let key in request) {
            if (request[key]) {
                formData.append(key, request[key]);
            }
        }
        const url = endpoints.manageUrl + 'projects';
        return appClient.post<string>(url, formData);
    }

    async updateProject(
        projectId: string,
        name: string,
        keywords: string[],
        color: string,
        featureFlags: string | null,
        smartIndexSettings: string | null
    ): Promise<ResultApi<unknown>> {
        const request = {
            name,
            keywords,
            color,
            featureFlags,
            smartIndexSettings
        };
        const formData = new FormData();

        for (let key in request) {
            if (request[key]) {
                formData.append(key, request[key]);
            }
        }

        const url = endpoints.manageUrl + `projects/${projectId}/edit`;
        return appClient.post(url, formData);
    }

    async exportProject(projectId: string, exportParts?: string[]) {
        const mapForm = document.createElement('form');
        mapForm.setAttribute('id', 'projectExportPostForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';

        if (exportParts && exportParts.length) {
            exportParts.forEach(o => {
                const exportOptionInput = document.createElement('input');
                exportOptionInput.type = 'text';
                exportOptionInput.name = 'exportParts[]';
                exportOptionInput.value = o;
                mapForm.appendChild(exportOptionInput);
            });
        }

        await security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            const url = endpoints.manageUrl + `projects/${projectId}/export${t}`;
            Utils.downloadFile(url, mapForm, 'projectExportPostForm');
            return Promise.resolve();
        });
    }

    async importProject(formData: FormData): Promise<ResultApi<ProjectImportModel>> {
        const url = endpoints.manageUrl + 'projects/import';
        return await appClient.post(url, formData);
    }

    deleteProject(projectId: string): Promise<ResultApi<unknown>> {
        const url = endpoints.manageUrl + `projects/${projectId}`;
        return appClient.delete(url);
    }

    async updateProjectMetadata(proj: Project, renameList: ProjectKeywordRenameModel[]): Promise<void> {
        const request = {
            projectId: proj.id,
            keywords: proj.keywords,
            renameList: renameList || []
        };

        const url = endpoints.manageUrl + `projects/${request.projectId}/update/metadata`;
        await appClient.update(url, request);
    }

    async getPackageLines(
        proj: Project,
        request: PackagesRequest,
        pkg?: Package | undefined
    ): Promise<PackageLinesResponse> {
        var { projectId, pkgId, search, pageSize, page, fieldsSearch, blockTypes, tags, searchPage } = request;

        if (!fieldsSearch) {
            fieldsSearch = {};
        }
        if (!blockTypes) {
            fieldsSearch.before = { blockType: 'LINE_BLOCK' };
        } else {
            if (blockTypes!.length > 1) {
                fieldsSearch.before = {
                    $or: blockTypes!.map(b => {
                        return { blockType: b };
                    })
                };
            } else {
                fieldsSearch.before = { blockType: (blockTypes!.length && blockTypes![0]) || 'LINE_BLOCK' };
            }
        }

        if (searchPage != null) {
            fieldsSearch.after = { page: searchPage };
        }
        if (tags && tags.length > 0) {
            fieldsSearch.packageMatch = { userTags: { $in: tags || [] } };
        }

        const url = `${endpoints.manageUrl}packages/search-lines`;
        const result = await appClient.post<SearchResult['searchPackageLines']>(url, {
            projectId,
            packageId: pkgId,
            searchTerm: !search || search.length === 0 ? null : search,
            pageSize,
            page: page || 0,
            packageState: PackageStateResult.Ready,
            fieldsSearch: fieldsSearch || null
        });
        const data = result.unwrapOr({ result: [], pageInfo: { total: 0 } });

        var response = new PackageLinesResponse();
        response.total = data.pageInfo.total;
        response.lines = data.result.map(r => {
            if (!pkg) {
                pkg = proj.packages.filter(p => p.id === r.packageId)[0];
            }
            const label = r.label ? proj.labels.find(l => l.text === r.label) : null;

            return new PackageLine(
                r.fieldId,
                pkg,
                r.rowId,
                r.text,
                r.normalizedText,
                label,
                r.additionalProperties,
                r.imagePath,
                new PageCoordinates(r.pageWidth, r.pageHeight, r.x, r.y, r.w, r.h, r.page),
                r.blockType || 'TEXTBOX_BLOCK'
            );
        });

        return response;
    }

    async getPackageNames(projectId: string, packageIds: string[]): Promise<{ packageId: string; name: string }[]> {
        if (packageIds.length === 0) {
            return [];
        }

        const documents: { packageId: string; name: string }[] = [];
        const uniquePackageIds = uniq(packageIds);
        const packageIdBatches = chunk(uniquePackageIds, 100);

        // Each batch fits in one page, avoiding unstable ordering between search pages.
        for (const packageIdBatch of packageIdBatches) {
            const result = await appClient.post<SearchPackagesRestResult>(`${endpoints.manageUrl}packages/search`, {
                projectIds: [projectId],
                packageIds: packageIdBatch,
                allSources: true,
                pageSize: packageIdBatch.length,
                page: 0
            });

            if (result.isErr()) {
                throw result.error;
            }

            const batchDocuments = result.value.packages.map(document => ({
                packageId: document.id,
                name: document.fileName
            }));

            documents.push(...batchDocuments);
        }

        return documents;
    }

    async searchPackages(proj: Project, request: SearchPackagesRequest): Promise<PackagesResponse> {
        let {
            projectId,
            search,
            pageSize,
            page,
            fieldsSearch,
            tags,
            allSources,
            orderBy,
            state,
            protectedOnly,
            uploadedBy,
            packageIds,
            states
        } = request;

        if (!fieldsSearch) {
            fieldsSearch = {
                packageMatch: {}
            };
        }

        if (tags && tags.length > 0) {
            fieldsSearch.packageMatch.userTags = { $in: tags || [] };
        }

        if (state && state.length > 0) {
            fieldsSearch.packageMatch.state = {
                $in: state.reduce((acc, value) => [...acc, ...stateFilterValues[value]], [])
            };
        }

        const url = `${endpoints.manageUrl}packages/search`;
        const result = await appClient.post<SearchPackagesRestResult>(url, {
            projectIds: [projectId],
            searchTerm: !search || search.length === 0 ? null : search,
            pageSize,
            page: page || 0,
            fieldsSearch: fieldsSearch || null,
            allSources: allSources,
            orderBy: orderBy || null,
            distinctBy: null,
            protectedOnly: protectedOnly,
            uploadedBy: uploadedBy,
            packageIds: packageIds,
            states: states
        });
        const data = result.unwrapOr({ packages: [], total: 0 });

        var response = new PackagesResponse();
        response.total = data.total;
        response.lines = data.packages.map(
            r =>
                new Package(
                    proj,
                    r.id,
                    r.fileName,
                    r.contentType,
                    r.state === PackageStateResult.Ready
                        ? PackageState.Ready
                        : r.state === PackageStateResult.Broken
                          ? PackageState.Broken
                          : PackageState.Busy,
                    r.filePath || null,
                    undefined,
                    r.userTags || [],
                    r.source || null,
                    r.uploadedTime || null,
                    r.indexDate || null,
                    r.error || null,
                    r.featureFlags ?? undefined,
                    r.isProtected ?? false,
                    r.currentOperation ?? null,
                    r.preparedSource ?? null,
                    r.publishedOutput ?? null,
                    r.errorDetails ?? null,
                    r.preparedSourceMatchesSettings ?? null
                )
        );

        return response;
    }

    async commitLabelsProject(request: CommitLabelsequest): Promise<void> {
        const url = endpoints.manageUrl + `projects/${request.projectId}/update/labels`;
        await appClient.update(url, request);
    }

    async getProjectTags(projectId?: string) {
        const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
        const result = await appClient.get<ProjectTagRestResult[]>(`${endpoints.manageUrl}projects/tags${query}`);
        return result.unwrapOr([]).map(x => x.tag);
    }

    handleDownload(id: string, fileType?: 'pdf' | 'apkg') {
        let path = encodeURIComponent(id);
        security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            let url = '';
            if (!fileType || fileType === 'pdf') {
                url = `${endpoints.manageUrl}document/${path}${t}`;
            } else {
                url = `${endpoints.manageUrl}file/${fileType}/${path}${t}`;
            }
            console.log(url);
            window.open(url);
            return Promise.resolve();
        });
    }

    async handleArchiveDownload(ids: string[], fileType: 'pdf' | 'apkg') {
        const mapForm = document.createElement('form');
        mapForm.setAttribute('id', 'downloadPostForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';
        ids.forEach(r => {
            const mapRuleIdInput = document.createElement('input');
            mapRuleIdInput.type = 'text';
            mapRuleIdInput.name = 'ids[]';
            mapRuleIdInput.value = r;
            mapForm.appendChild(mapRuleIdInput);
        });

        await security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            let url = '';
            if (fileType === 'pdf') {
                url = `${endpoints.manageUrl}document/${t}`;
            } else {
                url = `${endpoints.manageUrl}file/${fileType}/${t}`;
            }
            Utils.downloadFile(url, mapForm, 'downloadPostForm');
            return Promise.resolve();
        });
    }

    async updatePackageUserTags(pkg: Package, tags: string[]) {
        const url = endpoints.manageUrl + `packages/${pkg.id}/usertags`;
        await appClient.post(url, tags);
    }

    async savePackageProblemMessage(packageId: string, text: string, page: number) {
        const url = endpoints.manageUrl + `problems_report/${packageId}/${page}`;
        return appClient.post(url, { text });
    }

    async getPackageProblemMessages(projectId: string): Promise<PackageProblemReport[] | null> {
        const result = await appClient.get<PackageProblemReport[]>(
            `${endpoints.manageUrl}problems_report/projects/${projectId}`
        );
        if (result.isErr()) {
            console.log(result.error);
            throw result.error;
        }

        return result.value;
    }

    async setProblemStatus(status: string, recordId: string, problemId: string) {
        const url = endpoints.manageUrl + `problems_report/${recordId}/${problemId}/set_state`;
        await appClient.post(url, status);
    }

    async previewPackageFeatureFlags(packageId: string, featureFlags: Record<string, string> | null) {
        return appClient.post<FeatureFlagProcessingPlan>(
            `${endpoints.manageUrl}packages/${packageId}/feature-flags-plan`,
            featureFlags,
            undefined,
            { retry: false }
        );
    }

    async updatePackageFeatureFlags(
        packageId: string,
        featureFlags: Record<string, string> | null,
        plan: FeatureFlagProcessingPlan
    ) {
        return appClient.post<PackageResult>(
            `${endpoints.manageUrl}packages/${packageId}/save-feature-flags-and-run`,
            {
                featureFlags,
                expectedStage: plan.stage,
                planToken: plan.token
            },
            undefined,
            { retry: false }
        );
    }

    async previewPackagesFeatureFlags(packageIds: string[], featureFlags: Record<string, string> | null) {
        return appClient.post<PackageFeatureFlagsPreview[]>(
            `${endpoints.manageUrl}packages/feature-flags-plan-batch`,
            {
                packageIds,
                featureFlags
            },
            undefined,
            { retry: false }
        );
    }

    async savePackagesFeatureFlagsAndRun(
        packageIds: string[],
        featureFlags: Record<string, string> | null,
        plans: FeatureFlagProcessingPlan[]
    ) {
        return appClient.post<PackageProcessingResult[]>(
            `${endpoints.manageUrl}packages/save-feature-flags-batch-and-run`,
            {
                packageIds,
                featureFlags,
                plans: plans.map(plan => ({
                    packageId: plan.packageId,
                    expectedStage: plan.stage,
                    planToken: plan.token
                }))
            },
            undefined,
            { retry: false }
        );
    }

    async exportProjectFeautureFlags(projectId: string) {
        const mapForm = document.createElement('form');

        mapForm.setAttribute('id', 'exportProjectFeautureFlagsForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';

        await security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            const url = endpoints.manageUrl + `projects/${projectId}/export-feature-flags${t}`;
            Utils.downloadFile(url, mapForm, 'exportProjectFeautureFlagsForm');
            return Promise.resolve();
        });
    }

    async exportPackageFeautureFlags(packageId: string) {
        const mapForm = document.createElement('form');

        mapForm.setAttribute('id', 'exportPackageFeautureFlagsForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';

        await security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            const url = endpoints.manageUrl + `packages/${packageId}/export-feature-flags${t}`;
            Utils.downloadFile(url, mapForm, 'exportPackageFeautureFlagsForm');
            return Promise.resolve();
        });
    }

    private getProjectsUrl(state: PackageStateResult | null, scoped = false) {
        const query = state ? `?state=${encodeURIComponent(state)}` : '';
        return `${endpoints.manageUrl}projects${scoped ? '/scoped' : ''}${query}`;
    }

    private mapProjectResult(project: ProjectRestResult): ProjectResult {
        return {
            id: project.id,
            color: project.color,
            name: project.name,
            keywords: project.keywords,
            type: project.type || '',
            labels: project.labels || [],
            namedEntities: project.namedEntities || [],
            packages: project.packages || [],
            tagsVersion: project.tagsVersion,
            featureFlags: project.featureFlags || null,
            smartIndexSettings: project.smartIndexSettings || null
        };
    }
}
